import Dec from 'decimal.js';
import { EMPTY_BYTE_ARRAY } from 'constants/general';
import configJson from 'config/config.json';
import { displayFloats } from '../utils/numbersDisplay';
import { newContractReader } from './contractReader';

export const PARTICIPATION_FEE = 2;
export const MAX_POOL_PROJECTS = 10;

export const isEmptyBytes = (string) => string === EMPTY_BYTE_ARRAY;

export const uniqueArr = (value, index, self) => self.indexOf(value) === index;

export function sha512(str) {
  return crypto.subtle
    .digest('SHA-512', new TextEncoder('utf-8').encode(str))
    .then((buf) => Array.prototype.map.call(new Uint8Array(buf), (x) => `00${x.toString(16)}`.slice(-2)).join(''));
}

export const getTokenDecimals = async (contractAddress) => {
  const result = await newContractReader({ contractName: 'ERC20', functionName: 'decimals', contractAddress });

  return result;
};

export const requireAddress = (address) => {
  if (typeof address !== 'string') throw new Error('errors.address_is_not_a_string');
  if (address === '') throw new Error('errors.address_is_empty_string');
  if (address.length < 42) throw new Error('errors.address_too_short');
  if (isEmptyBytes(address)) throw new Error('errors.address_is_empty_bytes');
  if (!new RegExp(/0x[0-9a-fA-F]{40}/).test(address)) throw new Error('errors.invalid_address');
};

export const capitalize = (s) => {
  if (typeof s !== 'string') return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
};

/**
 * Mock method to simulate async call
 *
 * @param val {any}
 * @param time {Number}
 * @return {Promise<any>}
 */
export const wait = (time = 500, val = true) =>
  new Promise((resolve) => {
    setTimeout(() => {
      resolve(val);
    }, time);
  });

export const delayedAction = (ms = 500, action = () => {}) => {
  setTimeout(action, ms);
};

export const shortenAddress = (address) => `${address.slice(0, 5)}...${address.slice(-5)}`;

export const numberWithCommas = (x, decimals = 8, removeTrailingZeroes = true) => {
  if (!Number.isFinite(parseFloat(x))) return '0';
  if (parseFloat(x).toString() === '0') return '0';

  const parts = Dec(x).toFixed(decimals).split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  let formatted = parts.join('.');
  if (removeTrailingZeroes) {
    formatted = formatted
      .replace(/(\.\d*[1-9])0*$/, '$1') // 123.1200 -> 123.12
      .replace(/\.0*$/, '') // 123.00 -> 123
      .replace(/^0.0+$/, '0'); // 0.00 -> 0
  }
  return formatted;
};

export const formatNumber = (_num, decimals = 2, removeTrailingZeroes = true) => {
  try {
    if (!Number.isFinite(parseFloat(_num))) return '0';
    const sign = parseFloat(_num) < 0 ? '-' : '';
    const num = Math.abs(parseFloat(_num));
    if (num < 10000) return numberWithCommas(_num, decimals, removeTrailingZeroes);

    const si = [
      { value: 1, symbol: '' },
      { value: 1e3, symbol: 'k' },
      { value: 1e6, symbol: 'M' },
      { value: 1e9, symbol: 'B' },
      { value: 1e12, symbol: 'T' },
      { value: 1e15, symbol: 'P' },
      { value: 1e18, symbol: 'E' },
    ];
    let i;

    for (i = si.length - 1; i > 0; i -= 1) {
      if (num >= si[i].value) {
        break;
      }
    }

    const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
    return (
      sign +
      Dec(num / si[i].value)
        .toFixed(decimals)
        .replace(rx, '$1') +
      si[i].symbol
    );
  } catch (e) {
    return _num.toString();
  }
};

export const isTranslateString = (str) => /[\w+]\.[\w+]/gi.test(str) && !/\s+/g.test(str);

export const createTwitterLink = (handle) => `https://twitter.com/${handle}`;

export const isSsl = (str) => str && str.substr(0, 8) === 'https://';

export const validURL = (str) => {
  const pattern = new RegExp(
    '^(https?:\\/\\/)?' + // protocol
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
      '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
      '(\\#[-a-z\\d_]*)?$',
    'i',
  ); // fragment locator
  return !!pattern.test(str);
};

export const createEtherscanLink = (address) =>
  `https://${configJson.network === '3' ? 'ropsten.' : ''}etherscan.io/address/${address}`;
export const createBscscanLink = (address) =>
  `https://${configJson.network === '3' ? 'testnet.' : ''}bscscan.io/address/${address}`;

const createTokenScanLink = (address, scanLink) => `${scanLink}address/${address}`;

export const createTokenLink = (tokenAddress, tokenNetwork, scanLink) => {
  if (!tokenAddress) return '';
  return createTokenScanLink(tokenAddress, scanLink);
};

export const getPreviewForFileInput = (setPreviewFunc, files, index = 0) =>
  new Promise((resolve) => {
    const file = files[index];
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewFunc(e.target.result, index); // eslint-disable-line
      resolve(file);
    };
    reader.readAsDataURL(file);
  });

export const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

export const isVideo = (file) => file.type.split('/')[0] === 'video';

export const dataURLtoFile = (dataurl, filename) => {
  const arr = dataurl.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    // eslint-disable-line
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
};

// eslint-disable-next-line no-async-promise-executor
export const fileUrlToDataUrl = (url) =>
  new Promise(async (resolve, reject) => {
    try {
      const blob = await fetch(url).then((r) => r.blob());
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    } catch (err) {
      reject(err);
    }
  });

// eslint-disable-next-line no-async-promise-executor
export const convertUrlToFileSingle = (image) =>
  new Promise(async (resolve, reject) => {
    const { url, name } = image;
    try {
      // eslint-disable-next-line no-async-promise-executor
      const promise = new Promise(async (resolve2, reject2) => {
        try {
          const URI = await fileUrlToDataUrl(url);
          const file = dataURLtoFile(URI, name);
          resolve2({ file, preview: URI });
        } catch (err2) {
          reject2(err2);
        }
      });
      const res = await Promise.resolve(promise);
      resolve(res);
    } catch (err) {
      reject(err);
    }
  });

export const getPercentage = (num, total) => displayFloats((num / 100) * total);

export const deductFeeValue = (value = 0) => displayFloats(value - getPercentage(PARTICIPATION_FEE, value), 0);
